import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ENABLE_REACHABILITY, LICENSE_CONCLUDED } from '../../../../../shared/featureFlags/flagNames';
import { isLicensingIssueType } from '../../../../../shared/issuesV2';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import * as CommentsSelectors from '../../../../redux/comments/selectors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { icons } from '../../../generic/Icon';
import Tabs from '../../../generic/Tabs';
import { areAnyProjectsReachable } from '../../utils';
import type { ReduxStore } from '../../../../redux/store';

export type IssueDetailsTab = 'issue' | 'licenses' | 'projects' | 'reachability' | 'comments';

type Props = {
  activeTab: IssueDetailsTab;
  onChange: (tab: IssueDetailsTab) => void;
};

/**
 * IssueDetailsTabs is a somewhat generic Tabs component,
 * here it is specific to the issue drawer but could be
 * extracted and made more generic.
 */
const IssueDetailsTabs: VoidFunctionComponent<Props> = ({
  activeTab,
  onChange,
}) => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const commentsProjectId = useSelector(IssuesV2Store.selectors.getIssueCommentsProjectId);
  const commentsCount = useSelector((state: ReduxStore) =>
    CommentsSelectors.getCommentsCount(state, commentsProjectId));

  const affectedProjects = useSelector(IssuesV2Store.selectors.getAffectedProjects);

  const isReachabilityEnabled = useIsFeatureEnabled(ENABLE_REACHABILITY);

  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);
  const isLicensingIssue = issueDetails?.type && isLicensingIssueType(issueDetails.type);

  if (!issueDetails) {
    return null;
  }

  const affectedProjectCount = affectedProjects.length;
  const isReachable = isReachabilityEnabled && areAnyProjectsReachable(issueDetails.projects);

  return (
    <Tabs marginBottom="large">
      <Tabs.Tab
        label="Issue"
        icon={icons.bugRegular}
        isActive={activeTab === 'issue'}
        onSelect={() => onChange('issue')}
      />
      {isLicenseConcludedEnabled && isLicensingIssue && (
        <Tabs.Tab
          label="Licenses"
          icon={icons.fileOutline}
          isActive={activeTab === 'licenses'}
          onSelect={() => onChange('licenses')}
        />
      )}
      <Tabs.Tab
        label="Projects"
        icon={icons.folders}
        isActive={activeTab === 'projects'}
        onSelect={() => onChange('projects')}
        badgeCount={affectedProjectCount}
      />
      {isReachable && (
        <Tabs.Tab
          label="Reachability"
          icon={icons.code}
          isActive={activeTab === 'reachability'}
          onSelect={() => onChange('reachability')}
        />
      )}
      <Tabs.Tab
        label="Comments"
        icon={icons.comment}
        isActive={activeTab === 'comments'}
        onSelect={() => onChange('comments')}
        badgeCount={commentsCount}
      />
    </Tabs>
  );
};

export default IssueDetailsTabs;
